import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, Sparkles, X } from 'lucide-react';
import {
  CreditPackage,
  createPurchaseSession,
  getCreditPackages,
} from '../../services/api/creditPurchaseService';

interface CreditPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/* スタイルは CreditUsageModal と同じ「クラス文字列定数」方式で揃える。
 * overlay / container のアニメーションは globals.css の msc-slide-up を再利用。 */
const OVERLAY =
  'fixed top-0 left-0 right-0 bottom-0 bg-black/50 flex items-center justify-center ' +
  'z-[10000] p-5 backdrop-blur-[2px]';

const MODAL_CONTAINER =
  'bg-white rounded-tasky-lg w-full max-w-[720px] max-h-[85vh] ' +
  'shadow-[0_20px_25px_-5px_rgba(0,0,0,0.1)] flex flex-col overflow-hidden ' +
  'animate-[msc-slide-up_0.2s_ease-out]';

const MODAL_HEADER = 'flex justify-between items-center px-6 py-5 border-b border-b-gray-200 shrink-0'; /* #E5E7EB */

const MODAL_TITLE = 'flex items-center gap-2 text-[18px] font-semibold text-gray-900 m-0 [&_svg]:text-amber-500'; /* #111827 / #F59E0B */

const CLOSE_BUTTON =
  'bg-transparent border-0 p-1 cursor-pointer text-gray-500 rounded-tasky-xs flex ' +
  'items-center justify-center transition-all duration-150 hover:bg-gray-100 hover:text-gray-900'; /* #6B7280 / #F3F4F6 */

const MODAL_BODY = 'flex-1 min-h-0 overflow-y-auto p-6';

const LOADING_STATE = 'flex items-center justify-center gap-2 p-10 text-gray-500 text-[14px]';

const ERROR_STATE = 'p-5 text-center text-red-600 text-[14px]'; /* #DC2626 */

const PACKAGE_GRID = 'grid grid-cols-2 sm:grid-cols-3 gap-3';

const PACKAGE_CARD =
  'relative flex flex-col items-center pt-5 px-4 pb-5 border border-gray-200 ' +
  'rounded-tasky-md bg-[#FAFAFA] transition-all duration-150 ' +
  'hover:border-indigo-500 hover:bg-[#F5F3FF] hover:shadow-[0_2px_8px_rgba(99,102,241,0.1)]';

const DISCOUNT_BADGE =
  'absolute -top-2 -right-1 bg-red-600 text-white text-[11px] font-semibold ' +
  'px-2 py-0.5 rounded-[999px]';

const PACKAGE_CREDITS = 'text-[22px] font-bold text-gray-900 leading-[1.2]';

const PACKAGE_UNIT = 'text-[12px] text-gray-500 mb-2';

const PACKAGE_PRICE = 'text-[16px] font-semibold text-gray-700'; /* #374151 */

const PACKAGE_PRICE_TAX_NOTE = 'text-[11px] font-normal text-gray-500 ml-0.5'; /* #6B7280 */

const UNIT_PRICE = 'text-[11px] text-gray-400 mb-3'; /* #9CA3AF */

const PURCHASE_BUTTON =
  'w-full px-4 py-2 bg-indigo-500 text-white border-0 rounded-tasky-sm text-[13px] ' +
  'font-medium cursor-pointer transition-all duration-150 ' +
  'enabled:hover:bg-indigo-600 disabled:opacity-60 disabled:cursor-not-allowed'; /* #6366F1 / #4F46E5 */

const MODAL_FOOTER = 'px-6 py-4 border-t border-t-gray-200 shrink-0';

const FOOTER_NOTE = 'text-[12px] text-gray-400 text-center m-0';

const CreditPurchaseModal: React.FC<CreditPurchaseModalProps> = ({ isOpen, onClose }) => {
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [loading, setLoading] = useState(false);
  const [purchasingIndex, setPurchasingIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const loadPackages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCreditPackages();
      setPackages(data.packages);
    } catch (e) {
      console.error('Failed to load credit packages:', e);
      setError('パッケージの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    // 閉じている間に残った処理中状態・エラーを持ち越さない。
    setPurchasingIndex(null);
    loadPackages();
    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement as HTMLElement | null;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [isOpen, loadPackages]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  const handlePurchase = async (index: number) => {
    setPurchasingIndex(index);
    setError(null);
    try {
      const data = await createPurchaseSession(index);
      // Stripe Checkout へ全画面遷移する。戻り先は backend が
      // /credits/purchase/success (または /cancel) を success_url に指定済み。
      window.location.href = data.checkout_url;
    } catch (e) {
      console.error('Failed to create purchase session:', e);
      setError(e instanceof Error ? e.message : '購入セッションの作成に失敗しました。');
      setPurchasingIndex(null);
    }
  };

  const formatNumber = (n: number) => n.toLocaleString();

  const getUnitPrice = (pkg: CreditPackage) => (pkg.price / pkg.credits).toFixed(2);

  // 1クレジット = 1円 の最小パッケージを基準に、まとめ買いの割引率を出す。
  const getDiscount = (pkg: CreditPackage) => {
    const baseRate = 1;
    const actualRate = pkg.price / pkg.credits;
    if (actualRate >= baseRate) return 0;
    return Math.round((1 - actualRate) * 100);
  };

  if (!isOpen) return null;

  // Generation dialogs sit above the fixed credit badge; its purchase dialog
  // must escape that badge's stacking context when a generation returns 402.
  return createPortal(
    <div className={OVERLAY} onClick={onClose}>
      <div ref={modalRef} className={MODAL_CONTAINER} role="dialog" aria-modal="true" aria-label="クレジット追加購入" onClick={(e) => e.stopPropagation()}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.stopPropagation();
            onClose();
            return;
          }
          if (event.key !== 'Tab') return;
          const controls = Array.from(modalRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled), a[href]') || []);
          const first = controls[0];
          const last = controls[controls.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault(); last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault(); first?.focus();
          }
        }}>
        <div className={MODAL_HEADER}>
          <h2 className={MODAL_TITLE}>
            <Sparkles size={20} />
            クレジット追加購入
          </h2>
          <button ref={closeButtonRef} className={CLOSE_BUTTON} onClick={onClose} aria-label="閉じる">
            <X size={20} />
          </button>
        </div>

        <div className={MODAL_BODY}>
          {loading ? (
            <div className={LOADING_STATE}>
              <Loader2 size={24} className="animate-spin" />
              <span>パッケージを読み込んでいます...</span>
            </div>
          ) : error && packages.length === 0 ? (
            <div className={ERROR_STATE}>{error}</div>
          ) : (
            <>
              {error && <div className={ERROR_STATE}>{error}</div>}
              <div className={PACKAGE_GRID}>
                {packages.map((pkg, index) => {
                  const discount = getDiscount(pkg);
                  return (
                    <div className={PACKAGE_CARD} key={`${pkg.credits}-${pkg.price}`}>
                      {discount > 0 && <div className={DISCOUNT_BADGE}>{discount}% OFF</div>}
                      <div className={PACKAGE_CREDITS}>{formatNumber(pkg.credits)}</div>
                      <div className={PACKAGE_UNIT}>クレジット</div>
                      <div className={PACKAGE_PRICE}>
                        &yen;{formatNumber(pkg.price)}
                        <span className={PACKAGE_PRICE_TAX_NOTE}>税抜</span>
                      </div>
                      <div className={UNIT_PRICE}>&yen;{getUnitPrice(pkg)} / クレジット</div>
                      <button
                        className={PURCHASE_BUTTON}
                        onClick={() => handlePurchase(index)}
                        disabled={purchasingIndex !== null}
                      >
                        {purchasingIndex === index ? '処理中...' : '購入する'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className={MODAL_FOOTER}>
          <p className={FOOTER_NOTE}>
            表示価格は税抜です。決済時に消費税10%が加算されます。
          </p>
          <p className={FOOTER_NOTE}>
            決済はStripeで安全に処理されます。購入したクレジットは決済完了後に反映されます。
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CreditPurchaseModal;
